import { useState } from "react";

import { filter, find, isEmpty } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

import MultipleCalendarsWarning from "@app/analytics/components/MultipleCalendarsWarning";
import useCalendars from "@app/analytics/hooks/calendar/useCalendars";
import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import useConnectIntegrationToasts from "@app/analytics/Integrations/hooks/useConnectIntegrationToasts";
import { IntegrationDetailContent } from "@app/analytics/Integrations/styles";
import {
  List,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";
import type { UserCalendar } from "@app/analytics/types";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { isResponseErrorWithStatus, withQueryParams } from "@app/common/utils";

import { Link } from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import Switch from "@rui/Switch";
import TooltipIcon from "@rui/TooltipIcon";
import TooltipWrapper from "@rui/TooltipWrapper";

import { CALENDAR_SETTING_DESCRIPTIONS } from "./constants";
import DefaultCalendarButton from "./DefaultCalendarButton";
import DisconnectCalendarConfirmationModal from "./DisconnectCalendarConfirmationModal";
import RequireEditPermissionsConfirmationModal from "./RequireEditPermissionsConfirmationModal";
import {
  EditPermissionsRequiredAlert,
  ZoomAutomaticallyConnected,
} from "./styles";

const CalendarIntegrationConfig = ({
  providerId,
}: {
  providerId: CalendarProviderId;
}) => {
  const [confirmingCalendarDisconnect, setConfirmingCalendarDisconnect] =
    useState(false);
  const [confirmingEditPermissions, setConfirmingEditPermissions] =
    useState(false);

  const navigate = useNavigate();

  const {
    getEnableUrl,
    getIntegration,
    isLoading: isIntegrationsLoading,
  } = useIntegrations();
  const {
    connectedAccountProviders,
    connectedCalendarProviders,
    deleteConnectedProvider,
    isLoading: isConnectedProvidersLoading,
  } = useConnectedProviders();

  const { authorize, enable } = useIntegration();

  const { userSettings } = useUserSettings();
  const {
    calendarsForProvider: getCalendarsForProvider,
    toggleCalendar,
    isLoading: isCalendarsLoading,
  } = useCalendars();
  const { rssCalendar, updateRssLinkSettings } = useRssLinksSettings();

  const integration = getIntegration({ providerId, scope: "user" });
  const connectedProvider = find({ providerId }, connectedCalendarProviders);

  const providerConfig = CALENDAR_PROVIDERS_BY_ID[providerId];
  const providerContent = PROVIDER_CONTENT[providerId];

  useConnectIntegrationToasts({
    integration,
    targetAction: providerConfig.connectAction,
  });

  const isLoading =
    isConnectedProvidersLoading || isIntegrationsLoading || isCalendarsLoading;

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const handleConnectCalendar = () =>
    authorize.mutate({ providerId, returnRoute: location.pathname });

  const handleClickConnect = () => {
    const enableUrl = getEnableUrl({ providerId });
    if (enableUrl) {
      return enable.mutate(enableUrl, {
        onSuccess: () => {
          // simulate the connection success query params to trigger the toast
          navigate(
            withQueryParams(location.pathname, {
              action: providerConfig.connectAction,
              success: true,
            }),
            { replace: true }
          );
        },
        // if the connection wasn't able to be connected automatically, force
        // them through the auth flow immediately
        onError: () => handleConnectCalendar(),
      });
    }
    return handleConnectCalendar();
  };

  const handleDisconnectCalendar = () => {
    deleteConnectedProvider.mutate(providerId, {
      onSuccess: () => {
        setConfirmingCalendarDisconnect(false);
        toast.success(`Disconnected ${integration.displayName}`);
      },
      onError: () => {
        setConfirmingCalendarDisconnect(false);
        toast.error(
          `There was an error disconnecting ${integration.displayName}. Please try again or contact support@read.ai if the problem persists.`
        );
      },
    });
  };

  const handleClickDisconnect = () => setConfirmingCalendarDisconnect(true);

  const handleToggleCalendar = (calendar: UserCalendar, checked: boolean) => {
    const updatedCalendar = { ...calendar, isEnabled: checked };
    return toggleCalendar.mutate(updatedCalendar, {
      onError: () => {
        toast.error(
          "Something went wrong and your changes could not be saved. Please try again later or contact support@read.ai if the problem persists."
        );
      },
    });
  };

  const handleSetAsDefaultCalendar = () => {
    updateRssLinkSettings.mutate(
      { calendarProvider: providerId },
      {
        onError: (error) => {
          if (isResponseErrorWithStatus(error, 403)) {
            setConfirmingEditPermissions(true);
          } else if (isResponseErrorWithStatus(error, 422)) {
            toast.error(
              `${integration.displayName} default calendar is disabled. Please enable your ${integration.displayName} default calendar.`
            );
          } else {
            toast.error(
              "Something went wrong and your default calendar could not be updated. Please try again later or reach out to support@read.ai if the problem persists."
            );
          }
        },
      }
    );
  };

  const calendarsForProvider = getCalendarsForProvider(providerId);
  const enabledCalendars = filter("isEnabled", calendarsForProvider);
  const providerEmail = connectedProvider?.providerEmail;

  const wasAutomaticallyConnected =
    providerId === "zoom_calendar" &&
    integration.isConnected &&
    Boolean(find({ providerId: "zoom" }, connectedAccountProviders));

  const isRssLinksDefaultCalendar = rssCalendar?.provider === providerId;
  const canSetAsDefaultCalendar =
    providerConfig.supportsRssLinks && calendarsForProvider.length > 0;

  const showEditPermissionsRequiredAlert =
    integration.isConnected &&
    isRssLinksDefaultCalendar &&
    connectedProvider?.canEditCalendar === false;

  const connectCta = {
    onClick: handleClickConnect,
    isLoading: enable.isPending || enable.isError || authorize.isPending,
  };

  return (
    <>
      <MultipleCalendarsWarning kind="warning" />
      <IntegrationDetailHeader
        action={
          <DefaultCalendarButton
            disabled={!canSetAsDefaultCalendar}
            isDefault={isRssLinksDefaultCalendar}
            onClick={handleSetAsDefaultCalendar}
            show={providerConfig.supportsRssLinks}
          />
        }
        cta={connectCta}
        documentationUrl={providerConfig.documentationUrl}
        provider={integration}
        onClickDisconnect={handleClickDisconnect}
        // don't show duplicate alerts asking for reauth
        onReauth={
          showEditPermissionsRequiredAlert ? undefined : handleConnectCalendar
        }
        isDisconnecting={deleteConnectedProvider.isPending}
      >
        <Typography variant="bodySecondaryReg" color="shades.neutral.600">
          {providerContent.headerDescription ??
            providerContent.shortDescription}
        </Typography>
        {showEditPermissionsRequiredAlert && (
          <EditPermissionsRequiredAlert
            onClickReauthorize={handleConnectCalendar}
            displayName={integration.displayName}
          />
        )}
        {wasAutomaticallyConnected && <ZoomAutomaticallyConnected />}
      </IntegrationDetailHeader>
      {integration.isConnected ? (
        <IntegrationDetailContent spacing={3}>
          <Column spacing={1}>
            {integration.providerEmail && (
              <Typography variant="bodyPrimaryMed" color="shades.neutral.800">
                {integration.providerEmail} is connected
              </Typography>
            )}
            <Typography variant="bodySecondaryReg" color="shades.neutral.600">
              You have successfully connected Read to {integration.displayName}.
              Read can access and edit events on your calendar.
            </Typography>
          </Column>
          <Column spacing={1}>
            <Typography variant="bodySecondaryMed" color="shades.neutral.800">
              Your settings
            </Typography>
            <Typography variant="bodySecondaryReg" color="shades.neutral.600">
              <List>
                {CALENDAR_SETTING_DESCRIPTIONS.map((setting) => (
                  <li key={setting.id}>
                    {setting.getDescriptionFromSettings(userSettings)} (
                    <Link to={setting.manageUrl} fontWeight={400}>
                      Manage
                    </Link>
                    )
                  </li>
                ))}
              </List>
            </Typography>
          </Column>
          <Column spacing={1}>
            <Row variant="left" spacing={0.5}>
              <Typography variant="bodySecondaryMed" color="shades.neutral.800">
                Your synced calendars{" "}
              </Typography>
              <TooltipIcon
                title="At least one calendar must be synced"
                variant="help"
                iconColor="shades.primary.500"
              />
            </Row>

            <Column>
              {isEmpty(calendarsForProvider) ? (
                <Typography
                  variant="bodySecondaryReg"
                  color="shades.neutral.600"
                >
                  No calendars connected
                </Typography>
              ) : (
                calendarsForProvider.map((calendar) => {
                  const calendarDisableIsDisabled =
                    calendar.isEnabled && enabledCalendars.length === 1;

                  return (
                    <TooltipWrapper
                      key={calendar.summary}
                      show={calendarDisableIsDisabled}
                      title="At least one calendar must be synced"
                      placement="right"
                    >
                      <FormControlLabel
                        disabled={calendarDisableIsDisabled}
                        control={<Switch checked={calendar.isEnabled} />}
                        onChange={(e, checked) =>
                          handleToggleCalendar(calendar, checked)
                        }
                        label={
                          <Typography
                            variant="bodySecondaryReg"
                            color="shades.neutral.800"
                          >
                            {!calendar.summary ||
                            providerEmail === calendar.subscriberEmail
                              ? calendar.summary ?? calendar.subscriberEmail
                              : `${calendar.summary} (${calendar.subscriberEmail})`}
                          </Typography>
                        }
                        sx={{ ml: 0 }}
                      />
                    </TooltipWrapper>
                  );
                })
              )}
            </Column>
          </Column>
        </IntegrationDetailContent>
      ) : (
        <IntegrationNotConnected cta={connectCta} provider={integration} />
      )}

      {/* Modals */}
      {confirmingEditPermissions && (
        <RequireEditPermissionsConfirmationModal
          calendarDisplayName={integration.displayName}
          open
          onClose={() => setConfirmingEditPermissions(false)}
          onConfirm={handleConnectCalendar}
        />
      )}
      {confirmingCalendarDisconnect && (
        <DisconnectCalendarConfirmationModal
          calendar={integration}
          open
          onClose={() => setConfirmingCalendarDisconnect(false)}
          onConfirm={handleDisconnectCalendar}
          loading={deleteConnectedProvider.isPending}
        />
      )}
    </>
  );
};

export default CalendarIntegrationConfig;
